import type {
  ChartType,
  MetricValues,
  SecondaryDimensionValues,
} from '@revelio/core';

import { useGaChartPreviewMeta } from '../../../../global-ai-assistant/ga-chart-preview-meta-context';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import {
  shouldMaskAdaptiveEntityTransition,
  useAdaptiveChartType,
} from '../../../hooks/useAdaptiveChartType';
import { getRevelioPageOverrides } from '../../../utils/getRevelioPageOverrides';
import type {
  MultiMetricDataMap,
  MultiMetricProviderResult,
} from '../../DataProvider/MultiMetricProvider';
import {
  type DataProviderSet,
  DataType,
  isBoxBreakdownDataSet,
  isSentimentBarSingleDataSet,
  type SentimentBarSingleDataSet,
  type SingleDataPoint,
  type SingleDataSet,
} from '../../DataProvider/types';
import { usePageConfig } from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import type { PositionedItem } from '../../PageConfigProvider/types/item';
import {
  isNonHeadcountCompositionMetric,
  isPayMetric,
  isTransitionMetric,
} from '../../PageConfigProvider/types/metric';
import { Bar } from '../Bar';
import { Box as ChartBox } from '../Box';
import { ChartLoader } from '../ChartLoader';
import { ChartNotSupported } from '../ChartNotSupported';
import { Distribution } from '../Distribution';
import { DivergingBar } from '../DivergingBar';
import { DivergingBarCombo } from '../DivergingBarCombo';
import { Gauge } from '../Gauge';
import { GroupedBar } from '../GroupedBar/GroupedBar';
import { Line } from '../Line';
import { Metric } from '../Metric';
import { Sankey } from '../Sankey/Sankey';
import { StackedBar } from '../StackedBar';
import { TreeMap } from '../TreeMap';
import type { ParameterSelection } from '../utils/check-datapoint-in-parameter';
import { useComputedAxis } from '../utils/compute-axis-bounds';
import { getParameterAggregatedData } from '../utils/get-parameter-aggregated-data';
import { getPrimaryParameterSelections } from '../utils/get-primary-parameter-selections';
import { groupAndFilterData } from '../utils/group-and-filter-data';
import { Volume } from '../Volume';

const toSingleDataPoints = (
  data: SingleDataSet | SentimentBarSingleDataSet
): SingleDataPoint[] => {
  if (isSentimentBarSingleDataSet(data)) {
    return data.data.map((d) => ({
      dim: d.label ?? '',
      value: d.value,
      share: null,
      id: d.id ?? null,
    }));
  }
  return (data as SingleDataSet).data;
};

export const ChartSelector = ({
  chartType: baseChartType,
  data,
  item,
  metric,
  secondaryDimension,
  pageId,
  primaryParameterValues,
}: {
  chartType: ChartType;
  data: DataProviderSet;
  item: PositionedItem;
  metric?: MetricValues;
  secondaryDimension?: SecondaryDimensionValues;
  pageId?: string;
  primaryParameterValues?: ParameterSelection[];
}) => {
  const gaPreviewMeta = useGaChartPreviewMeta();
  const {
    chartType: hookChartType,
    activePrimaryFilterLength,
    isAdaptiveChartMode,
  } = useAdaptiveChartType(item);
  const previewActsAsAdaptive = gaPreviewMeta !== null;
  const adaptiveOrPreview = isAdaptiveChartMode || previewActsAsAdaptive;

  const metricFromStackedBreakdownSingle = ((): SingleDataPoint[] | null => {
    if (
      !adaptiveOrPreview ||
      baseChartType !== 'stacked-bar' ||
      activePrimaryFilterLength !== 1 ||
      data?.type !== DataType.BREAKDOWN_SINGLE
    ) {
      return null;
    }
    const points = data.data.flatMap((row) => row.breakdown);
    if (points.length !== 1) {
      return null;
    }
    return points;
  })();

  const metricFromBarSingle = ((): SingleDataPoint[] | null => {
    if (
      !adaptiveOrPreview ||
      baseChartType !== 'bar' ||
      activePrimaryFilterLength !== 1 ||
      data === undefined ||
      (data.type !== DataType.SINGLE && !isSentimentBarSingleDataSet(data))
    ) {
      return null;
    }
    const points = toSingleDataPoints(
      data as SingleDataSet | SentimentBarSingleDataSet
    );
    if (points.length !== 1) {
      return null;
    }
    return points;
  })();

  const metricShortcutPoints =
    metricFromStackedBreakdownSingle ?? metricFromBarSingle;

  const chartType =
    metricShortcutPoints !== null
      ? ('metric' as ChartType)
      : (hookChartType ?? baseChartType);

  const willMaskAdaptive = shouldMaskAdaptiveEntityTransition({
    isAdaptiveChartMode,
    baseChartType,
    activePrimaryFilterLength,
    data,
  });

  if (willMaskAdaptive) {
    return <ChartLoader />;
  }

  if (chartType === 'metric' && metricShortcutPoints !== null) {
    return <Metric data={metricShortcutPoints} metric={metric} />;
  }

  if (chartType === 'gauge' && data?.type === DataType.SINGLE) {
    const gaugeMetricKey =
      (item.plot?.defaultMetricOptions?.[0] as MetricValues) ??
      ('value' as MetricValues);
    const multiData: MultiMetricDataMap = { [gaugeMetricKey]: data };
    return (
      <Gauge
        chartId={item.id}
        valueTemplate={item.plot?.gaugeValue ?? ''}
        primaryText={item.plot?.gaugePrimaryText ?? ''}
        primaryLabel={item.plot?.gaugePrimaryLabel ?? ''}
        subText={item.plot?.gaugeSubText ?? ''}
        minLabel={item.plot?.gaugeMinLabel}
        maxLabel={item.plot?.gaugeMaxLabel}
        multiData={multiData}
        colorPalette={item.plot?.colorPalette}
      />
    );
  }

  if (chartType === 'metric' && data?.type === DataType.SINGLE) {
    return (
      <Metric
        data={
          isSentimentBarSingleDataSet(data)
            ? toSingleDataPoints(data)
            : data.data
        }
        metric={metric}
      />
    );
  }

  if (chartType === 'bar' && data?.type === DataType.SINGLE) {
    const { maxValue, adjustedMin } = getRevelioPageOverrides(item);

    const coloredBars = [
      RevelioPageId.COMPANY_SENTIMENT,
      RevelioPageId.ROLE_SENTIMENT,
      RevelioPageId.GEOGRAPHY_SENTIMENT,
    ].includes(pageId as RevelioPageId);

    const barPoints = toSingleDataPoints(data);

    return (
      <Bar
        data={barPoints}
        metric={metric}
        adjustedMin={adjustedMin}
        maxValue={maxValue}
        coloredBars={!!coloredBars && !data.meta?.transformed}
        color={data.meta?.transformed ? undefined : item?.plot?.color}
        showDataLabels={item?.plot?.showDataLabels}
        primaryParameterValues={primaryParameterValues}
        sortByValue={(primaryParameterValues?.length ?? 0) > 0}
        comparisonTransformed={data.meta?.transformed}
      />
    );
  }

  if (chartType === 'line' && data?.type === DataType.TIMESERIES) {
    return (
      <Line
        data={data.data}
        displayAs={item.plot?.displayAs}
        primaryParameterValues={primaryParameterValues}
      />
    );
  }

  if (chartType === 'line' && data?.type === DataType.BREAKDOWN_TIMESERIES) {
    return (
      <Line
        data={data.data.map((d) => ({
          dim: d.dim,
          timeseries: d.breakdown,
        }))}
        displayAs={item.plot?.displayAs}
        primaryParameterValues={primaryParameterValues}
      />
    );
  }

  if (chartType === 'sankey' && data?.type === DataType.BREAKDOWN_SINGLE) {
    return <Sankey data={data.data} />;
  }

  if (
    chartType === 'treemap' &&
    (data?.type === DataType.BREAKDOWN_SINGLE || data?.type === DataType.SINGLE)
  ) {
    const treemapData =
      data.type === DataType.SINGLE ? toSingleDataPoints(data) : data.data;
    return <TreeMap data={treemapData} metric={metric} />;
  }
  if (chartType === 'stacked-bar' && data?.type === DataType.BREAKDOWN_SINGLE) {
    return (
      <StackedBar
        data={data.data}
        metric={metric}
        hideLegend={
          isTransitionMetric(metric) &&
          [
            RevelioPageId.COMPANY_TRANSITIONS,
            RevelioPageId.GEOGRAPHY_TRANSITIONS,
            RevelioPageId.ROLE_TRANSITIONS,
          ].includes(pageId as RevelioPageId)
        }
      />
    );
  }
  if (chartType === 'grouped-bar' && data?.type === DataType.BREAKDOWN_SINGLE) {
    return (
      <GroupedBar
        data={data.data}
        hideLegend={[
          RevelioPageId.ROLE_POSTINGS,
          RevelioPageId.GEOGRAPHY_POSTINGS,
          RevelioPageId.COMPANY_POSTINGS,
        ].includes(pageId as RevelioPageId)}
        ignoreCustomTooltip={
          isPayMetric(metric) || isNonHeadcountCompositionMetric(metric)
        }
      />
    );
  }
  if (
    chartType === 'diverging-bar' &&
    data?.type === DataType.BREAKDOWN_SINGLE
  ) {
    if (data.data.length !== 2) {
      return (
        <ChartNotSupported
          errorDetails={{
            chartId: item.id,
            chartTitle: item.title ?? undefined,
            chartType,
            metric,
            secondaryDimension,
            reason: 'chart_not_supported',
          }}
        >
          Diverging bar requires exactly two entities selected.
        </ChartNotSupported>
      );
    }
    return <DivergingBar dataSet={data} />;
  }
  if (chartType === 'diverging-bar' && data?.type === DataType.SINGLE) {
    if (isSentimentBarSingleDataSet(data)) {
      const normalized: SingleDataSet = {
        type: DataType.SINGLE,
        data: toSingleDataPoints(data),
        ...(data.meta !== undefined ? { meta: data.meta } : {}),
      };
      return <DivergingBar dataSet={normalized} />;
    }
    return <DivergingBar dataSet={data as SingleDataSet} />;
  }
  if (chartType === 'volume' && data?.type === DataType.TIMESERIES) {
    return <Volume data={data.data} />;
  }
  if (
    data?.type === DataType.BOX &&
    chartType === 'box' &&
    isBoxBreakdownDataSet(data)
  ) {
    return (
      <ChartBox
        data={data.data}
        primaryParameterValues={primaryParameterValues}
      />
    );
  }
  if (data?.type === DataType.DISTRIBUTION && chartType === 'distribution') {
    const { colorOverride } = getRevelioPageOverrides(item);

    return <Distribution data={data.data} colorOverride={colorOverride} />;
  }
  if (
    chartType === 'diverging-bar-combo' &&
    data?.type === DataType.BREAKDOWN_SINGLE
  ) {
    if (data.data.length !== 2) {
      return (
        <ChartNotSupported
          errorDetails={{
            chartId: item.id,
            chartTitle: item.title ?? undefined,
            chartType,
            metric,
            secondaryDimension,
            reason: 'chart_not_supported',
          }}
        >
          Diverging bar combo requires exactly two entities selected.
        </ChartNotSupported>
      );
    }
    return <DivergingBarCombo dataSet={data} />;
  }

  return (
    <ChartNotSupported
      errorDetails={{
        chartId: item.id,
        chartTitle: item.title ?? undefined,
        chartType,
        metric,
        secondaryDimension,
        reason: 'chart_not_supported',
      }}
    />
  );
};

export const MultiMetricBodyChartSelector = ({
  item,
  chartType,
  data,
  metric,
}: {
  item: PositionedItem;
  chartType: ChartType;
  data: MultiMetricProviderResult;
  metric?: MetricValues;
}) => {
  const { config: pageConfig } = usePageConfig();
  const { activeFilterMap } = useFilterContext();

  const multiDataForAxis: MultiMetricDataMap | undefined =
    chartType === 'line' && data.byMetric
      ? (() => {
          const reportParameters =
            pageConfig.tabs?.[0]?.tabContent?.reportParameters ?? [];
          const plotParameters = item?.plot?.plotParameters ?? [];
          const out: MultiMetricDataMap = {};
          (Object.keys(data.byMetric) as MetricValues[]).forEach(
            (metricKey) => {
              const dataset = data.byMetric[metricKey];
              if (dataset) {
                out[metricKey] = getParameterAggregatedData({
                  data: dataset,
                  plotParameters,
                  reportParameters,
                  activeFilterMap,
                });
              }
            }
          );
          return out;
        })()
      : undefined;

  const computedYAxis = useComputedAxis({
    yAxisMin: item?.plot?.yAxisMin,
    yAxisMax: item?.plot?.yAxisMax,
    multiData: multiDataForAxis,
  });

  if (chartType === 'gauge') {
    const gaugeValue = item.plot?.gaugeValue ?? '';
    const gaugePrimaryText = item.plot?.gaugePrimaryText ?? '';
    const gaugePrimaryLabel = item.plot?.gaugePrimaryLabel ?? '';
    const gaugeSubText = item.plot?.gaugeSubText ?? '';
    return (
      <Gauge
        chartId={item.id}
        valueTemplate={gaugeValue}
        primaryText={gaugePrimaryText}
        primaryLabel={gaugePrimaryLabel}
        subText={gaugeSubText}
        minLabel={item.plot?.gaugeMinLabel}
        maxLabel={item.plot?.gaugeMaxLabel}
        multiData={data.byMetric}
        colorPalette={item.plot?.colorPalette}
      />
    );
  }

  if (chartType === 'line' && data.byMetric && multiDataForAxis) {
    const lineMetric: MetricValues | undefined =
      metric ?? item.plot?.defaultMetricOptions?.[0];
    const reportParameters =
      pageConfig.tabs?.[0]?.tabContent?.reportParameters ?? [];
    const plotParameters = item?.plot?.plotParameters ?? [];

    const dataset = lineMetric ? data.byMetric[lineMetric] : undefined;

    if (dataset) {
      const aggregatedData = getParameterAggregatedData({
        data: dataset,
        plotParameters,
        reportParameters,
        activeFilterMap,
      });
      const { groupBySegment, plotFilters } = item?.plot ?? {};
      const filteredData = groupAndFilterData({
        data: aggregatedData,
        groupBySegment,
        plotFilters,
      });
      const primaryParams = getPrimaryParameterSelections({
        reportParameters,
        activeFilterMap,
      });

      if (filteredData?.type === DataType.TIMESERIES) {
        return (
          <Line
            data={filteredData.data}
            displayAs={item.plot?.displayAs}
            primaryParameterValues={primaryParams}
            yAxis={computedYAxis}
          />
        );
      }

      if (filteredData?.type === DataType.BREAKDOWN_TIMESERIES) {
        return (
          <Line
            data={filteredData.data.map((d) => ({
              dim: d.dim,
              timeseries: d.breakdown,
            }))}
            displayAs={item.plot?.displayAs}
            primaryParameterValues={primaryParams}
            yAxis={computedYAxis}
          />
        );
      }
    }
  }

  return (
    <ChartNotSupported
      errorDetails={{
        chartId: item.id,
        chartTitle: item.title ?? undefined,
        chartType,
        reason: 'chart_not_supported',
      }}
    />
  );
};
